import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CheckCircle2, CreditCard, Mail, Upload, ShieldCheck, Clock, Video, Edit3, X, ThumbsUp, RotateCcw, FileText, Tag } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const REASON_OPTIONS = [
  { key: "out_of_price_range", label: "Out of my price range" },
  { key: "change_material", label: "I'd like a different material" },
  { key: "change_design", label: "I'd like to change the design" },
  { key: "something_else", label: "Something else — I'll explain" },
];

function ProofUploader({ orderId, onDone }) {
  const fileRef = useRef();
  const [preview, setPreview] = useState(null);
  const [fileKind, setFileKind] = useState(null); // 'image' | 'pdf'
  const [fileName, setFileName] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ok = ["image/jpeg", "image/png", "image/jpg", "image/webp", "image/heic", "image/heif", "application/pdf"].includes(file.type);
    if (!ok) { toast.error("Please upload a JPG, PNG, or PDF file."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large — please keep it under 5MB."); return; }
    setFileName(file.name);
    setFileKind(file.type === "application/pdf" ? "pdf" : "image");
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!preview) { toast.error("Please choose a file first."); return; }
    setSending(true);
    try {
      await api.post(`/orders/${orderId}/military-proof`, { image_data_url: preview, note });
      toast.success("Proof uploaded securely. We'll verify shortly.");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setSending(false); }
  };

  return (
    <div className="mt-4" data-testid="proof-uploader">
      <input ref={fileRef} data-testid="proof-file-input" type="file"
             accept="image/jpeg,image/png,image/jpg,image/webp,application/pdf,.jpg,.jpeg,.png,.pdf" onChange={onFile} className="hidden"/>
      <div className="flex flex-wrap gap-3 items-center">
        <Button data-testid="proof-choose-file" type="button" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-full">
          <Upload size={14} className="mr-2"/>{preview ? "Choose a different file" : "Choose JPG, PNG or PDF"}
        </Button>
        {preview && fileKind === "image" && (
          <img src={preview} alt="proof preview" data-testid="proof-preview" className="h-14 w-14 rounded border hairline object-cover"/>
        )}
        {preview && fileKind === "pdf" && (
          <div data-testid="proof-preview-pdf" className="h-14 px-4 flex items-center gap-2 rounded border hairline bg-white text-sm">
            <FileText size={16} style={{color: "var(--accent-metal)"}}/><span>{fileName}</span>
          </div>
        )}
      </div>
      <textarea
        data-testid="proof-note"
        value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="Optional — tell us who it is (e.g., 'I'm the spouse of SSG J. Reed, US Army')"
        className="mt-3 w-full text-sm rounded border hairline p-3 bg-white resize-none" rows={2}
      />
      <Button data-testid="proof-submit" onClick={submit} disabled={sending} className="mt-3 rounded-full btn-primary px-6">
        {sending ? "Uploading…" : "Submit proof"}
      </Button>
    </div>
  );
}

function QuoteActions({ order, onUpdated }) {
  const [mode, setMode] = useState(null); // 'alt' | 'cancel' | null
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const final = (order.quote_amount_after_discount ?? order.quote_amount ?? order.amount ?? 0) / 100;

  const act = async (action, payload = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.order_id}/respond`, { action, ...payload });
      toast.success({
        approve: "Quote approved — thank you!",
        request_alternative: "Alternative requested — we'll reply shortly.",
        cancel: "Order cancelled.",
      }[action]);
      onUpdated?.(data.status);
      setMode(null); setReason(""); setNote("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit response");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="quote-actions" className="mt-10 rounded-lg border hairline p-8 bg-white">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase text-[11px] tracking-[0.3em]" style={{color: "var(--accent-metal)"}}>Studio Quote</div>
          <div className="font-serif text-4xl mt-2" style={{color: "var(--primary)"}} data-testid="quote-total">${final.toFixed(2)}</div>
          {order.quote_note && <p className="mt-2 text-sm text-[color:var(--text-muted)] italic">"{order.quote_note}"</p>}
          {order.discount_code_applied && (
            <div className="mt-2 text-xs" style={{color: "var(--accent-metal)"}}>
              Code applied: <b>{order.discount_code_applied}</b> ({order.discount_code_percent}% off)
            </div>
          )}
          {order.military_discount_amount ? (
            <div className="mt-1 text-xs" style={{color: "#4F7A60"}}>10% Military/First Responder discount applied</div>
          ) : null}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="approve-quote-btn" disabled={busy} onClick={() => act("approve")} className="rounded-full btn-primary">
            <ThumbsUp size={14} className="mr-2"/>Approve quote
          </Button>
          <Button data-testid="request-alt-btn" disabled={busy} variant="outline" onClick={() => setMode(mode === "alt" ? null : "alt")} className="rounded-full">
            <RotateCcw size={14} className="mr-2"/>Request alternative
          </Button>
          <Button data-testid="cancel-order-btn" disabled={busy} variant="ghost" onClick={() => setMode(mode === "cancel" ? null : "cancel")} className="rounded-full text-rose-700 hover:text-rose-900 hover:bg-rose-50">
            <X size={14} className="mr-2"/>Cancel order
          </Button>
        </div>
      </div>

      {mode === "alt" && (
        <div data-testid="alt-form" className="mt-6 pt-6 border-t hairline">
          <div className="text-sm font-semibold" style={{color: "var(--primary)"}}>Request an alternative</div>
          <div className="mt-3">
            <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">Reason<span className="text-rose-700"> *</span></label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="alt-reason" className="h-10 mt-1 bg-white"><SelectValue placeholder="Please pick a reason"/></SelectTrigger>
              <SelectContent>
                {REASON_OPTIONS.map((r) => <SelectItem key={r.key} value={r.key} data-testid={`reason-${r.key}`}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Textarea data-testid="alt-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Tell us more (optional, except for 'something else')" className="mt-3 bg-white"/>
          <div className="mt-4 flex gap-2">
            <Button data-testid="alt-submit" disabled={busy || !reason} onClick={() => act("request_alternative", { reason, note })} className="rounded-full btn-primary">Send request</Button>
            <Button variant="ghost" onClick={() => setMode(null)} className="rounded-full">Nevermind</Button>
          </div>
        </div>
      )}

      {mode === "cancel" && (
        <div data-testid="cancel-form" className="mt-6 pt-6 border-t hairline">
          <div className="text-sm font-semibold text-rose-800">Cancel this order?</div>
          <p className="text-xs text-[color:var(--text-muted)] mt-1">This cannot be undone from here. You can always start a new project any time.</p>
          <Textarea data-testid="cancel-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional — reason for cancelling (helps us improve)" className="mt-3 bg-white"/>
          <div className="mt-4 flex gap-2">
            <Button data-testid="cancel-submit" disabled={busy} onClick={() => act("cancel", { note })} className="rounded-full bg-rose-700 hover:bg-rose-800 text-white">Yes, cancel order</Button>
            <Button variant="ghost" onClick={() => setMode(null)} className="rounded-full">Keep order</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function DiscountCodeInput({ order, onApplied }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.order_id}/apply-discount`, { code });
      toast.success(`${data.code} applied — ${data.percent_off}% off`);
      onApplied?.();
      setCode("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply code");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="discount-input" className="mt-4 flex items-center gap-2">
      <Tag size={14} style={{color: "var(--accent-metal)"}}/>
      <Input data-testid="discount-code" placeholder="Have a code? Enter it here" value={code} onChange={(e) => setCode(e.target.value)} className="h-9 max-w-[220px] uppercase bg-white"/>
      <Button data-testid="discount-apply" size="sm" disabled={busy || !code.trim()} onClick={apply} className="rounded-full">Apply</Button>
    </div>
  );
}

function DesignEditor({ order, onSaved }) {
  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState(order?.description || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDesc(order?.description || ""); }, [order?.description]);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${order.order_id}/design-change`, { description: desc });
      toast.success("Design update sent — we'll take a look.");
      onSaved?.();
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save change");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="design-editor" className="mt-6 rounded-lg border hairline p-6 bg-white">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="uppercase text-xs tracking-[0.3em]" style={{color: "var(--accent-metal)"}}>Your design & text</div>
          <p className="text-sm mt-2 text-[color:var(--text-muted)] leading-relaxed">{order?.description}</p>
        </div>
        <Button data-testid="edit-design-btn" variant="outline" onClick={() => setOpen((o) => !o)} className="rounded-full">
          <Edit3 size={14} className="mr-2"/>{open ? "Close editor" : "Alter design or text"}
        </Button>
      </div>
      {open && (
        <div className="mt-4" data-testid="design-editor-panel">
          <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">Describe your updated design</label>
          <Textarea data-testid="design-edit-text" rows={5} value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1 bg-white"/>
          <p className="text-xs text-[color:var(--text-muted)] mt-2">We'll get an email the moment you save. If this changes the price, we'll resend you a new quote.</p>
          <Button data-testid="design-edit-save" disabled={busy} onClick={save} className="mt-3 rounded-full btn-primary">Save & notify studio</Button>
        </div>
      )}
    </div>
  );
}

export default function QuoteReceived() {
  const { order_id } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [savingCard, setSavingCard] = useState(false);
  const cardSavedParam = params.get("card_saved");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${order_id}`);
      setOrder(data);
    } catch (e) {
      console.error("Failed to load order", e);
      toast.error("Unable to load this order. Please refresh or sign in again.");
    }
  }, [order_id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (cardSavedParam === "1") toast.success("Card saved on file — we'll charge only once you approve the quote.");
    if (cardSavedParam === "0") toast.info("Card save cancelled — you can try again anytime.");
  }, [cardSavedParam]);

  const saveCard = async () => {
    setSavingCard(true);
    try {
      const { data } = await api.post("/payments/save-card", { order_id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start card capture");
      setSavingCard(false);
    }
  };

  const cardOnFile = order?.card_on_file || cardSavedParam === "1";
  const isQuoteOrder = order?.type === "quote";
  const canRespond = isQuoteOrder && (order?.status === "quoted" || order?.status === "pending_quote");
  const hasResponded = isQuoteOrder && ["approved", "alternative_requested", "cancelled"].includes(order?.status);
  const quoteSent = isQuoteOrder && order?.status === "quoted";

  return (
    <div data-testid="quote-received-page" className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
      <div className="flex items-center gap-3 text-[color:var(--accent-metal)]">
        <CheckCircle2 size={20} strokeWidth={1.75}/>
        <div className="uppercase text-xs tracking-[0.3em] font-bold">
          {quoteSent ? "Quote ready for review" : hasResponded ? "Response recorded" : "Quote request received"}
        </div>
      </div>
      <h1 className="font-serif text-5xl mt-3 leading-tight" style={{color: "var(--primary)"}}>
        {quoteSent ? "Your quote is ready." : hasResponded ? "Thanks — we got your response." : "Thank you — we're on it."}
      </h1>
      <p className="mt-4 text-[color:var(--text-muted)]">
        Order <span className="font-mono text-sm" style={{color:"var(--primary)"}}>{order_id}</span>
        {!quoteSent && !hasResponded && " · you'll hear back within one business day with a firm quote and a timeline."}
        {hasResponded && order?.status === "approved" && " · Approved. We'll charge your saved card and start production."}
        {hasResponded && order?.status === "alternative_requested" && " · We'll review your note and send you a revised quote shortly."}
        {hasResponded && order?.status === "cancelled" && " · Cancelled. No charges will be made."}
      </p>

      {canRespond && order && (
        <>
          {quoteSent && <QuoteActions order={order} onUpdated={() => load()}/>}
          {quoteSent && <DiscountCodeInput order={order} onApplied={load}/>}
        </>
      )}

      {isQuoteOrder && order && order.status !== "cancelled" && order.status !== "delivered" && order.status !== "approved" && (
        <DesignEditor order={order} onSaved={load}/>
      )}

      {order?.design_preview_image && (
        <div data-testid="design-preview-block" className="mt-6 rounded-lg border hairline bg-white p-6">
          <div className="uppercase text-xs tracking-[0.3em]" style={{color: "var(--accent-metal)"}}>Your design preview</div>
          <p className="text-xs text-[color:var(--text-muted)] mt-2">The exact composition we received from your session in the Design Studio.</p>
          <img src={order.design_preview_image} alt="your design preview" className="mt-4 max-w-full rounded border hairline"/>
        </div>
      )}

      {!quoteSent && (
        <div className="mt-10 grid sm:grid-cols-3 gap-4">
          <div className="rounded-lg border hairline p-5 bg-white"><Mail size={16} strokeWidth={1.75} style={{color: "var(--accent-metal)"}}/>
            <div className="font-serif text-lg mt-2" style={{color: "var(--primary)"}}>Quote via email</div>
            <div className="text-xs text-[color:var(--text-muted)] mt-1">Firm price + timeline, within 24h.</div>
          </div>
          <div className="rounded-lg border hairline p-5 bg-white"><Video size={16} strokeWidth={1.75} style={{color: "var(--accent-metal)"}}/>
            <div className="font-serif text-lg mt-2" style={{color: "var(--primary)"}}>Optional video chat</div>
            <div className="text-xs text-[color:var(--text-muted)] mt-1">Free call with the engraver if details need clarifying.</div>
          </div>
          <div className="rounded-lg border hairline p-5 bg-white"><Clock size={16} strokeWidth={1.75} style={{color: "var(--accent-metal)"}}/>
            <div className="font-serif text-lg mt-2" style={{color: "var(--primary)"}}>You approve, we charge</div>
            <div className="text-xs text-[color:var(--text-muted)] mt-1">Nothing is billed until you say yes.</div>
          </div>
        </div>
      )}

      {/* CARD ON FILE */}
      <div data-testid="card-on-file-block" className="mt-10 rounded-lg border hairline p-8" style={{background: cardOnFile ? "rgba(197,160,89,0.08)" : "white"}}>
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{background: "rgba(197,160,89,0.14)", color: "var(--accent-metal)"}}>
            {cardOnFile ? <CheckCircle2 size={18} strokeWidth={1.75}/> : <CreditCard size={18} strokeWidth={1.75}/>}
          </div>
          <div className="flex-1">
            <div className="font-serif text-2xl" style={{color: "var(--primary)"}}>
              {cardOnFile ? "Card on file — all set." : "Save a card on file (optional, but speeds things up)"}
            </div>
            <p className="mt-2 text-sm text-[color:var(--text-muted)] leading-relaxed">
              {cardOnFile
                ? "We'll charge your saved card only after you approve the quote and the final production details."
                : "We'll securely save your card via Stripe. Nothing is charged until we agree on your quote. You can also do this later from your Dashboard."}
            </p>
            {!cardOnFile && (
              <Button data-testid="save-card-btn" onClick={saveCard} disabled={savingCard} className="mt-5 rounded-full px-6 py-6 btn-primary">
                {savingCard ? "Opening Stripe…" : "Save a card securely"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {order?.military_discount && !order?.military_proof_submitted && (
        <div data-testid="military-pending" className="mt-6 rounded-lg border hairline p-6" style={{background: "rgba(197,160,89,0.08)"}}>
          <div className="flex items-start gap-3">
            <ShieldCheck size={18} strokeWidth={1.75} style={{color: "var(--accent-metal)"}} className="mt-0.5 shrink-0"/>
            <div className="flex-1">
              <div className="text-sm font-semibold" style={{color: "var(--primary)"}}>10% Military / First Responder discount pending</div>
              <div className="text-xs text-[color:var(--text-muted)] mt-1 leading-relaxed">
                Upload a photo <b>or PDF</b> of a valid military ID, DD-214, agency ID, or an immediate-family document (dependent ID, marriage certificate, or similar). We review all submissions privately — files are not shown publicly.
              </div>
              <ProofUploader orderId={order_id} onDone={() => setOrder({...order, military_proof_submitted: true})}/>
            </div>
          </div>
        </div>
      )}
      {order?.military_proof_submitted && (
        <div data-testid="military-proof-ok" className="mt-6 rounded-lg border hairline p-5 flex items-start gap-3" style={{background: "rgba(79,122,96,0.08)"}}>
          <CheckCircle2 size={18} strokeWidth={1.75} style={{color: "#4F7A60"}} className="mt-0.5 shrink-0"/>
          <div>
            <div className="text-sm font-semibold" style={{color: "var(--primary)"}}>Proof received — thank you</div>
            <div className="text-xs text-[color:var(--text-muted)] mt-1">We'll verify your submission and apply the 10% discount to your quote.</div>
          </div>
        </div>
      )}

      {hasResponded && order?.customer_response && (
        <div data-testid="response-recap" className="mt-6 rounded-lg border hairline p-5 bg-white">
          <div className="uppercase text-[11px] tracking-[0.3em]" style={{color: "var(--accent-metal)"}}>Your response</div>
          <div className="font-serif text-xl mt-2" style={{color: "var(--primary)"}}>
            {{
              approve: "You approved this quote",
              request_alternative: "You requested an alternative",
              cancel: "You cancelled this order",
            }[order.customer_response.action]}
          </div>
          {order.customer_response.reason && (
            <div className="text-sm mt-1 text-[color:var(--text-muted)]">Reason: {REASON_OPTIONS.find((r) => r.key === order.customer_response.reason)?.label || order.customer_response.reason}</div>
          )}
          {order.customer_response.note && <div className="text-sm mt-1 text-[color:var(--text-muted)] italic">"{order.customer_response.note}"</div>}
        </div>
      )}

      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/dashboard"><Button data-testid="go-dashboard" variant="outline" className="rounded-full px-6 py-6">View in dashboard</Button></Link>
        <Link to="/gallery"><Button variant="outline" className="rounded-full px-6 py-6">Browse the gallery</Button></Link>
      </div>
    </div>
  );
}
